export interface HandbookFieldDiff {
  field: string;
  before: string;
  after: string;
}

export type HandbookDiffToken = { type: 'same' | 'del' | 'ins'; text: string };

export interface HandbookTrackBlock {
  tokens: HandbookDiffToken[];
  changed: boolean;
}

export function stripHandbookHtml(html: string): string {
  return String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Keep link URLs in the plain text so href-only edits show in Track Changes. */
export function handbookHtmlToPlain(html: string): string {
  const withLinks = String(html || '').replace(
    /<a\b[^>]*href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi,
    (_m, href, inner) => {
      const text = String(inner)
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return text ? ` ${text} ⟦${href}⟧ ` : ` ⟦${href}⟧ `;
    },
  );
  return stripHandbookHtml(withLinks);
}

export function handbookHtmlToBlocks(html: string): string[] {
  const withLinks = String(html || '').replace(
    /<a\b[^>]*href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi,
    (_m, href, inner) => {
      const text = String(inner)
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return text ? `${text} ⟦${href}⟧` : `⟦${href}⟧`;
    },
  );
  return withLinks
    .replace(/<\/(p|h[1-6]|li|div|section|header|blockquote|tr)>/gi, '\n')
    .replace(/<(t[dh])\b[^>]*>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .split(/\n+/)
    .map(s => s.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

export function handbookFieldsChanged(
  before: { title: string; effectiveDate: string; location: string; body: string },
  after: { title: string; effectiveDate: string; location: string; body: string },
): HandbookFieldDiff[] {
  const rows: HandbookFieldDiff[] = [];
  const pairs: Array<[string, string, string]> = [
    ['Title', before.title, after.title],
    ['Effective', before.effectiveDate, after.effectiveDate],
    ['Location', before.location, after.location],
    ['Body', handbookHtmlToPlain(before.body), handbookHtmlToPlain(after.body)],
  ];
  for (const [field, a, b] of pairs) {
    if ((a || '').trim() !== (b || '').trim()) {
      rows.push({ field, before: a || '—', after: b || '—' });
    }
  }
  return rows;
}

export function coalesceHandbookDiff(tokens: HandbookDiffToken[]): HandbookDiffToken[] {
  const out: HandbookDiffToken[] = [];
  for (const t of tokens) {
    if (!t.text) continue;
    const last = out[out.length - 1];
    if (last && last.type === t.type) {
      last.text = `${last.text} ${t.text}`.replace(/\s+/g, ' ').trim();
    } else {
      out.push({ type: t.type, text: t.text });
    }
  }
  return out;
}

/** Word-level tokens for a simple inline Track Changes mark-up. */
export function diffHandbookText(before: string, after: string): HandbookDiffToken[] {
  const a = before.trim().split(/\s+/).filter(Boolean);
  const b = after.trim().split(/\s+/).filter(Boolean);
  const out: HandbookDiffToken[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ type: 'same', text: a[i] });
      i += 1;
      j += 1;
      continue;
    }
    const laterInB = b.indexOf(a[i], j);
    const laterInA = a.indexOf(b[j], i);
    if (laterInB !== -1 && (laterInA === -1 || laterInB - j <= laterInA - i)) {
      out.push({ type: 'ins', text: b[j] });
      j += 1;
    } else if (laterInA !== -1) {
      out.push({ type: 'del', text: a[i] });
      i += 1;
    } else {
      out.push({ type: 'del', text: a[i] });
      out.push({ type: 'ins', text: b[j] });
      i += 1;
      j += 1;
    }
  }
  while (i < a.length) {
    out.push({ type: 'del', text: a[i] });
    i += 1;
  }
  while (j < b.length) {
    out.push({ type: 'ins', text: b[j] });
    j += 1;
  }
  return coalesceHandbookDiff(out);
}

function lcsIndexPairs(a: string[], b: string[]): Array<[number, number]> {
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const pairs: Array<[number, number]> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      pairs.push([i, j]);
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i += 1;
    } else {
      j += 1;
    }
  }
  return pairs;
}

function diffHandbookBlocks(beforeBlocks: string[], afterBlocks: string[]): HandbookTrackBlock[] {
  const matches = lcsIndexPairs(beforeBlocks, afterBlocks);
  const out: HandbookTrackBlock[] = [];
  let i = 0;
  let j = 0;
  let mi = 0;
  while (i < beforeBlocks.length || j < afterBlocks.length) {
    const next = matches[mi];
    if (next && i === next[0] && j === next[1]) {
      out.push({ tokens: [{ type: 'same', text: afterBlocks[j] }], changed: false });
      i += 1;
      j += 1;
      mi += 1;
      continue;
    }
    if (next && i < next[0] && j < next[1]) {
      out.push({ tokens: diffHandbookText(beforeBlocks[i], afterBlocks[j]), changed: true });
      i += 1;
      j += 1;
      continue;
    }
    if (!next || i < (next?.[0] ?? beforeBlocks.length)) {
      if (i < beforeBlocks.length) {
        out.push({ tokens: [{ type: 'del', text: beforeBlocks[i] }], changed: true });
        i += 1;
        continue;
      }
    }
    if (j < afterBlocks.length) {
      out.push({ tokens: [{ type: 'ins', text: afterBlocks[j] }], changed: true });
      j += 1;
    }
  }
  return out;
}

/** Paragraph-aware Track Changes. Falls back to a full-document word diff when HTML structure disagrees. */
export function diffHandbookDocument(beforeHtml: string, afterHtml: string): HandbookTrackBlock[] {
  const beforeBlocks = handbookHtmlToBlocks(beforeHtml);
  const afterBlocks = handbookHtmlToBlocks(afterHtml);
  const beforePlain = handbookHtmlToPlain(beforeHtml);
  const afterPlain = handbookHtmlToPlain(afterHtml);

  if (beforeBlocks.length === 0 && afterBlocks.length === 0) return [];
  if (beforePlain === afterPlain) {
    return afterBlocks.map(text => ({ tokens: [{ type: 'same', text }], changed: false }));
  }

  const blockDiff = diffHandbookBlocks(beforeBlocks, afterBlocks);
  const unchanged = blockDiff.filter(block => !block.changed).length;
  const matchRatio = unchanged / Math.max(beforeBlocks.length, afterBlocks.length, 1);
  if (blockDiff.length === 0 || matchRatio < 0.2) {
    return [{ tokens: diffHandbookText(beforePlain, afterPlain), changed: true }];
  }
  return blockDiff;
}
